import * as React from "react";
import { Check, Copy } from "lucide-react";

interface ColorSwatchProps {
  name: string;
  utility: string;
  /** Fallback hexes (used only before the live values are read post-mount). */
  hexLight: string;
  hexDark: string;
  /** Live values read from the actual CSS tokens, when available. */
  live?: { light: string; dark: string };
}

function useIsDark(): boolean {
  const [isDark, setIsDark] = React.useState(false);

  React.useEffect(() => {
    const root = document.documentElement;
    const update = () => setIsDark(root.classList.contains("dark"));
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

/**
 * Reads the live light + dark values of CSS custom properties straight from the
 * stylesheet rules (the :root / @theme declaration for light, the .dark rule for
 * dark). Mode-independent — it never depends on which theme is currently active —
 * so the design-system reference can never drift from the real tokens.
 */
export function useLiveTokens(
  cssVars: string[],
): Record<string, { light: string; dark: string }> {
  const [vals, setVals] = React.useState<
    Record<string, { light: string; dark: string }>
  >({});
  const key = cssVars.join(",");

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const out: Record<string, { light: string; dark: string }> = {};
    cssVars.forEach((v) => (out[v] = { light: "", dark: "" }));

    // Recurse: Tailwind v4 nests the @theme :root inside @layer blocks, so we must
    // descend into grouping rules (@layer / @media / @supports), not just top-level.
    const visit = (rules: CSSRuleList) => {
      for (const rule of Array.from(rules)) {
        if (rule instanceof CSSStyleRule) {
          const parts = (rule.selectorText || "").split(",").map((s) => s.trim());
          const isDark = parts.some((s) => s === ".dark" || s.startsWith(".dark "));
          const isRoot = parts.some((s) => s === ":root" || s === ":host");
          if (!isDark && !isRoot) continue;
          for (const v of cssVars) {
            const val = rule.style.getPropertyValue(v).trim();
            if (!val) continue;
            if (isDark) out[v].dark = val;
            else out[v].light = val;
          }
        } else if ("cssRules" in rule && (rule as CSSGroupingRule).cssRules) {
          try {
            visit((rule as CSSGroupingRule).cssRules);
          } catch {
            // ignore nested-access errors
          }
        }
      }
    };
    for (const sheet of Array.from(document.styleSheets)) {
      let rules: CSSRuleList | null = null;
      try {
        rules = sheet.cssRules;
      } catch {
        continue; // cross-origin sheet — skip
      }
      if (rules) visit(rules);
    }
    setVals(out);
  }, [key]);

  return vals;
}

interface CopyableHexProps {
  label: string;
  hex: string;
  isActive: boolean;
}

function CopyableHex({ label, hex, isActive }: CopyableHexProps) {
  const [copied, setCopied] = React.useState(false);

  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(hex);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // ignore
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Copy ${label} mode value ${hex}`}
      className={[
        "group flex w-full cursor-pointer items-center justify-between gap-2 rounded-md px-1.5 py-1 font-mono text-[10px] transition-colors",
        "hover:bg-surface",
        isActive ? "text-ink-display" : "text-ink-muted",
      ].join(" ")}
    >
      <span className="text-ink-muted">{label}</span>
      <span className="flex items-center gap-1.5">
        <span>{hex}</span>
        {copied ? (
          <Check className="h-3 w-3 text-accent" aria-hidden />
        ) : (
          <Copy
            className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100"
            aria-hidden
          />
        )}
      </span>
    </button>
  );
}

export function ColorSwatch({
  name,
  utility,
  hexLight,
  hexDark,
  live,
}: ColorSwatchProps) {
  const isDark = useIsDark();
  // Live values are the source of truth once read; the props are only a pre-hydration fallback.
  const light = live?.light || hexLight;
  const dark = live?.dark || hexDark;
  const currentHex = isDark ? dark : light;

  return (
    <div className="flex flex-col gap-2">
      <div
        className="aspect-[2/1] rounded-md border border-hairline"
        style={{ backgroundColor: currentHex }}
        aria-label={`${name} (${isDark ? "dark" : "light"} mode preview)`}
      />
      <div className="text-xs">
        <div className="font-medium text-ink-display">{name}</div>
        <div className="font-mono text-ink-muted">{utility}</div>
        <div className="mt-1 flex flex-col gap-0.5">
          <CopyableHex label="Light" hex={light} isActive={!isDark} />
          <CopyableHex label="Dark" hex={dark} isActive={isDark} />
        </div>
      </div>
    </div>
  );
}
